import {Subject} from 'rxjs';
import {Table} from 'dexie';
import {SyncItem} from '../interfaces/core/sync-item';
import {inject} from '@angular/core';
import {SyncStatusItemsTable} from './sync-status-items.table';
import { v4 as uuidv4 } from 'uuid';
import {DateTime} from 'luxon';
import {TableSyncSettings} from '../interfaces/core/table-sync-settings';
import {db} from './db.service';
import {TableName} from '../enums/core/table-name';

export abstract class SyncTable<T extends SyncItem> {

  protected _tableSyncSettings: TableSyncSettings = null;

  private readonly _change$: Subject<void>;
  private readonly _table: Table<T, string>;

  private readonly _syncStatusItemsTable: SyncStatusItemsTable;

  protected _lockChangeTrigger: boolean = false;

  protected constructor(table: Table<T, string>) {
    this._table = table;
    this._change$ = new Subject<void>();
    this._syncStatusItemsTable = inject(SyncStatusItemsTable);
  }

  get change$(): Subject<void> {
    return this._change$;
  }

  get table(): Table<T, string> {
    return this._table;
  }

  get tableSyncSettings(): TableSyncSettings {
    return this._tableSyncSettings;
  }

  /**
   *
   */
  public async clear(): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      await this._table.clear();
      await this._syncStatusItemsTable.removeByTableName(this._table.name);
    });
    this.triggerChange();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Single item
  // -------------------------------------------------------------------------------------------------------------------
  /**
   *
   * @param itemUuid
   */
  public async get(itemUuid: string): Promise<T> {
    return this._table.get(itemUuid);
  }

  /**
   *
   * @param item
   * @param isSynced
   */
  public async add(item: T, isSynced: boolean = false): Promise<T> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      item = this.setBasicProperties(item);
      await this._table.add(item);
      await this._syncStatusItemsTable.create(this._table.name as TableName, item.uuid, isSynced);
    });
    this.triggerChange();
    return item;
  }

  /**
   *
   * @param item
   * @param isSynced
   */
  public async update(item: T, isSynced: boolean = false): Promise<T> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      item = this.setUpdateProperties(item, isSynced);
      await this._table.put(item);
      // addOrUpdate, damit ein Edit auch dann überlebt, wenn der Queue-Eintrag zwischenzeitlich
      // entfernt wurde (z. B. durch einen Kunden-Refresh mit force-remove)
      await this._syncStatusItemsTable.addOrUpdate(this._table.name as TableName, item.uuid, isSynced);
    });
    this.triggerChange();
    return item;
  }

  /**
   *
   * @param item
   * @param isSynced
   */
  public async addOrUpdate(item: T, isSynced: boolean = false): Promise<T> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      const _item = await this._table.get(item.uuid);
      item = _item ? this.setUpdateProperties(item, isSynced) : this.setBasicProperties(item);
      await this._table.put(item);
      await this._syncStatusItemsTable.addOrUpdate(this._table.name as TableName, item.uuid, isSynced);
    });
    this.triggerChange();
    return item;
  }

  /**
   *
   * @param itemUuid
   * @param withoutSync
   */
  public async remove(itemUuid: string, withoutSync: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      await this._table.delete(itemUuid);
      await this._syncStatusItemsTable.remove(itemUuid, withoutSync);
    });
    this.triggerChange();
  }
  // -------------------------------------------------------------------------------------------------------------------
  // Multiple items
  // -------------------------------------------------------------------------------------------------------------------
  public async set(items: T[]): Promise<void> {
    this._lockChangeTrigger = true;
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      await this.clear();
      await this.addMany(items, true);
    });
    this._lockChangeTrigger = false;
    this.triggerChange();
  }

  /**
   *
   */
  public async getAll(): Promise<T[]> {
    return this._table.toArray();
  }

  /**
   *
   * @param itemUuids
   */
  public async getItems(itemUuids: string[] = []): Promise<T[]> {
    const _items = await this._table.bulkGet(itemUuids);
    return _items.filter(_item => _item !== undefined);
  }

  /**
   *
   * @param items
   * @param isSynced
   */
  public async addMany(items: T[], isSynced: boolean = false): Promise<T[]> {
    await db.transaction('rw', db.syncStatusItems, this._table, async() => {
      items = items.map(_item => this.setBasicProperties(_item));
      await this._table.bulkAdd(items);
      await this._syncStatusItemsTable.createMany(
        this._table.name as TableName,
        items.map(_item => _item.uuid),
        isSynced
      );
    });
    this.triggerChange();
    return items;
  }

  /**
   *
   * @param items
   * @param isSynced
   */
  public async updateMany(items: T[], isSynced: boolean = false): Promise<T[]> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      items = items.map(_item => this.setUpdateProperties(_item));
      items = items.filter(_item => !!_item.uuid);
      const uuids = await this._table.bulkPut(items, { allKeys: true });
      await this._syncStatusItemsTable.updateMany(this._table.name as TableName, uuids, isSynced);
    });
    this.triggerChange();
    return items;
  }

  /**
   *
   * @param items
   * @param isSynced
   */
  public async addOrUpdateMany(items: T[], isSynced: boolean = false): Promise<T[]> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      items = items.map(_item => {
        return _item.uuid ? this.setUpdateProperties(_item, isSynced) : this.setBasicProperties(_item);
      });
      const uuids = await this._table.bulkPut(items, { allKeys: true });
      await this._syncStatusItemsTable.addOrUpdateMany(this._table.name as TableName, uuids, isSynced);
    });
    this.triggerChange();
    return items;
  }

  /**
   *
   * @param itemUuids
   * @param withoutSynced - If true, the items will be removed without synchronisation
   */
  public async removeMany(itemUuids: string[], withoutSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, this._table, async () => {
      await this._syncStatusItemsTable.removeMany(itemUuids, withoutSynced);
      await this._table.bulkDelete(itemUuids);
    });
    this.triggerChange();
  }
  // -------------------------------------------------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------------------------------------------------

  /**
   *
   */
  public async isSynced(): Promise<boolean> {
    return await this._syncStatusItemsTable.isSynced([this._table.name]);
  }

  /**
   *
   * @param itemUuid
   */
  public async isSyncedByUuid(itemUuid: string): Promise<boolean> {
    return await this.isSyncedByUuids([itemUuid]);
  }

  /**
   *
   * @param itemUuids
   */
  public async isSyncedByUuids(itemUuids: string[]): Promise<boolean> {
    return await this._syncStatusItemsTable.isSyncedByPrimaryKeys(this._table.name as TableName, itemUuids);
  }

  /**
   *
   */
  public async getSynced(): Promise<T[]> {
    const syncStatusItems =
      await this._syncStatusItemsTable.getSynced(this._table.name as TableName);
    return this.getItems(syncStatusItems.map(_syncStatusItem => _syncStatusItem.primaryKey));
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Functions
  // -------------------------------------------------------------------------------------------------------------------
  /**
   *
   * @param item
   * @private
   */
  private setBasicProperties(item: T): T {
    item.uuid = item.uuid ? item.uuid : uuidv4();
    item.created = item.created ? item.created : DateTime.local().toISO();
    item.modified = item.modified ? item.modified : DateTime.local().toISO();
    return item;
  }

  /**
   *
   * @param item
   * @param isSynced
   * @private
   */
  private setUpdateProperties(item: T, isSynced: boolean = false): T {
    item.modified = isSynced ? item.modified : DateTime.local().toISO();
    return item;
  }

  /**
   * 
   * @private
   */
  protected triggerChange(): void {
    if(!this._lockChangeTrigger) {
      this.change$.next();
    }
  }

}
